import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  Row,
  Col,
  Container,
  Card,
  Button,
  Badge,
} from 'react-bootstrap'
import Meta from '../components/Meta'
import { loadCourses } from '../actions/courseActions'
import { FeatureCards } from '../components/landing/FeatureCards'

/**
 * LandingScreen: Clean, functional landing page for Pixagan focusing on Laeyerz and Pixagan Learn.
 */
export const LandingScreenV2 = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const courselist = useSelector((state) => state.courselist)
  const { courses } = courselist || {}

  const [sectionType] = useState('all')

  useEffect(() => {
    dispatch(loadCourses())
  }, [dispatch])

  const openExternal = (url) => window.open(url, '_blank')

  return (
    <div style={{ fontFamily: 'Inter, Roboto, "Open Sans", Arial, sans-serif', background: '#f7f8fb' }}>
      <Meta
        title="Pixagan · Laeyerz"
        description="Laeyerz: graph-based visual agent workflow builder. Pixagan Learn: practical courses on Deep Learning, LLMs, RAG, and Agents."
      />

      {/* HERO */}
      <header
        style={{
          background: 'linear-gradient(90deg, #fff 55%, #f3f5ff 100%)',
          padding: '64px 0',
          borderBottom: '1px solid rgba(12,8,84,0.06)',
        }}
      >
        <Container>
          <Row className="align-items-center">
            <Col md={7}>
              <h1 style={{ fontSize: '2.4rem', fontWeight: 800, color: '#0C0854', marginBottom: 12 }}>
                Laeyerz — Agentic AI Workflow Builder
              </h1>
              <p style={{ fontSize: '1.05rem', color: '#333', lineHeight: 1.6, maxWidth: 680 }}>
                Build agentic, graph-based workflows visually and run them with Python. Laeyerz makes it easy to compose
                LLMs, vector stores, tools and agents into reliable, maintainable AI workflows.
              </p>

              <div className="d-flex gap-3 mt-4" style={{ flexWrap: 'wrap' }}>
                <Button
                  onClick={() => openExternal('https://laeyerz.com')}
                  variant="primary"
                  style={{ borderRadius: 12, padding: '10px 18px', fontWeight: 600 }}
                >
                  Get Started
                </Button>
                <Button
                  onClick={() => openExternal('https://github.com/pixagan/Laeyerz')}
                  variant="outline-secondary"
                  style={{ borderRadius: 12, padding: '10px 18px', fontWeight: 600 }}
                >
                  View on GitHub
                </Button>
                <Button
                  onClick={() => navigate('/docs/getting-started')}
                  variant="link"
                  style={{ padding: '10px 8px', color: '#0C0854' }}
                >
                  Docs
                </Button>
              </div>
            </Col>

            <Col md={5} className="text-center">
              <img
                src="/pixagan.png"
                alt="Pixagan / Laeyerz"
                style={{
                  width: '260px',
                  maxWidth: '60%',
                  borderRadius: 16,
                  boxShadow: '0 8px 30px rgba(12,8,84,0.08)',
                }}
              />
            </Col>
          </Row>
        </Container>
      </header>

      {/* LAYERZ EXAMPLES */}
      <section style={{ padding: '48px 0' }}>
        <Container>
          <h2 style={{ color: '#0C0854', fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>
            Examples: Build with Laeyerz
          </h2>
          <p style={{ textAlign: 'center', color: '#444', marginBottom: 28 }}>
            Visual, composable nodes that connect LLMs, knowledge stores, agents and tools.
          </p>

          <Row className="g-4 justify-content-center">
            <Col md={6} lg={4}>
              <FeatureCards
                header="Graph-based Workflows"
                description="Create a workflow, add functional nodes and execute the workflow."
                icon="📦"
                code={`test_flow = Flow()
llmNode = OpenAILLMNode(name="llmNode", config={"api_key": api_key})
vecstore = ChromaDBNode(name="vecstore")
test_flow.add_node(llmNode)
test_flow.add_node(vecstore)`}
              />
            </Col>

            <Col md={6} lg={4}>
              <FeatureCards
                header="Agents in your Workflow"
                description="Embed agents to orchestrate tools and provide intelligent behavior."
                icon="🤖"
                code={`agent = ReActAgent(
    name="AgentSimple", 
    api_key_path=api_key_path,
    model="gpt-5-mini", 
    role="You are a travel planner", 
    instructions="Use the tools to provide the best response", 
    tools={}
)`}
              />
            </Col>

            <Col md={6} lg={4}>
              <FeatureCards
                header="RAG + Vector Stores"
                description="Combine retrieval with LLMs to ground answers in your data."
                icon="📚"
                code={`flow.add_node(ChromaDBNode(...))
flow.add_node(RetrievalQA(name="rag", retriever=vec_retriever))`}
              />
            </Col>
          </Row>
        </Container>
      </section>

      <hr style={{ border: 0, height: 1, background: 'rgba(12,8,84,0.06)' }} />

      {/* PIXAGAN LEARN */}
      <section style={{ padding: '48px 0', background: '#fff' }}>
        <Container>
          <h2 style={{ color: '#0C0854', fontWeight: 700, marginBottom: 8, textAlign: 'center' }}>
            Pixagan Learn
          </h2>
          <p style={{ textAlign: 'center', color: '#444', marginBottom: 24 }}>
            Hands-on courses on Deep Learning, LLMs, Retrieval-Augmented Generation, Agents and more.
          </p>

          <Row xs={1} md={2} lg={3} className="g-4">
            {courses && courses.length > 0 ? (
              courses.map((course) => (
                <Col key={course.url}>
                  <Link to={`/courses/${course.url}`} style={{ textDecoration: 'none' }}>
                    <Card
                      style={{
                        borderRadius: 14,
                        boxShadow: '0 6px 18px rgba(12,8,84,0.06)',
                        border: 'none',
                        minHeight: 380,
                      }}
                      className="h-100"
                    >
                      <div style={{ height: 160, overflow: 'hidden', borderTopLeftRadius: 14, borderTopRightRadius: 14 }}>
                        <Card.Img
                          src={course.cover}
                          alt={course.title}
                          style={{ height: '100%', objectFit: 'cover' }}
                        />
                      </div>

                      <Card.Body>
                        <div className="d-flex align-items-start">
                          <div style={{ flex: 1 }}>
                            <h5 style={{ color: '#0C0854', marginBottom: 6 }}>{course.title}</h5>
                            <p style={{ color: '#444', marginBottom: 10 }}>{course.description}</p>
                          </div>
                          {!course.isPosted && (
                            <Badge bg="secondary" style={{ height: 32, alignSelf: 'flex-start' }}>
                              Coming Soon
                            </Badge>
                          )}
                        </div>
                        <div className="mt-3">
                          <Button variant="outline-primary" size="sm" onClick={() => navigate(`/courses/${course.url}`)}>
                            View Course
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Link>
                </Col>
              ))
            ) : (
              <Col>
                <Card style={{ borderRadius: 12, padding: 24, textAlign: 'center' }}>
                  <p style={{ margin: 0 }}>Courses are loading or unavailable. Check back soon.</p>
                </Card>
              </Col>
            )}
          </Row>
        </Container>
      </section>

      <footer style={{ padding: '28px 0', textAlign: 'center', color: '#6b6b8a' }}>
        <Container>
          <div style={{ marginBottom: 8 }}>
            © {new Date().getFullYear()} Pixagan · <a href="https://laeyerz.com" target="_blank" rel="noreferrer">Laeyerz</a>
          </div>
        </Container>
      </footer>
    </div>
  )
}

export default LandingScreenV2